import { useFetcher, useLoaderData } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import { PaymentTablePageLoaderData } from "./loaders/PaymentTablePageLoader";
import { Breadcrumb, Button, Card, Modal, Space } from "antd";
import { CalendarOutlined, HomeOutlined } from "@ant-design/icons";
import {
  CustomTable,
  CustomTextField,
  MoneyText,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { PaymentStatusTag, PaymentTablePageFilters, ViewReceiptButton } from "./components";
import { ColumnsType } from "antd/es/table";
import {
  INTENTS,
  ROUTES,
  compareTransactionCreatedAt,
  compareTransactionPeriod,
  compareTransactionUpdatedAt,
  dateToLocalTime,
  defaultTransactionSort,
  formatDisplayDateOnly,
} from "~/utils";
import { FormattedTransactionObject } from "~/types";
import * as APITypes from "~/API";
import { TableRowSelection } from "antd/es/table/interface";
import React, { useMemo } from "react";
import { canTerminateTransaction } from "~/services/server";
import { UploadFileButton } from "../MyDocumentPage/components";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { TFunction } from "i18next";

export const getPaymentTableColumns = (
  t: TFunction,
  handleOpenModal: (transaction: APITypes.Transaction) => void
): ColumnsType<FormattedTransactionObject> => [
    {
      title: t("transaction_ID"),
      dataIndex: "id",
      key: "id",
      width: 250,
    },
    {
      title: t("Entity_Info"),
      dataIndex: "entity",
      key: "entity",
      width: 200,
      render: (_, record: FormattedTransactionObject) => {
        const entityID = record.details.entityID;
        const entityType = record.details.entityType;
        let entityName = "";
        if (entityType === APITypes.EntityType.Unit) {
          entityName = record.Unit?.name || "";
        }
        if (entityType === APITypes.EntityType.Building) {
          entityName = record.Building?.name || "";
        }
        return (
          <CustomTextField
            prefix={`${entityType}: `}
            value={entityName}
            link={`/${entityType}/${entityID}`}
            newPage={true}
          />
        );
      },
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      width: 200,
      sorter: (a, b) =>
        (a.details.status || "").localeCompare(b.details.status || ""),
      sortDirections: ["ascend", "descend"],
      render: (_, record: FormattedTransactionObject) => {
        return <PaymentStatusTag status={record.details.status} />;
      },
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      key: "amount",
      width: 200,
      sorter: (a, b) => a.details.amount - b.details.amount,
      sortDirections: ["ascend", "descend"],
      render: (_, record) => {
        return <MoneyText value={record.details.amount} />;
      },
    },
    {
      title: t("Reason"),
      dataIndex: "reason",
      key: "reason",
      width: 200,
      sorter: (a, b) => compareTransactionPeriod(a, b),
      sortDirections: ["ascend", "descend"],
      defaultSortOrder: "descend",
      render: (_, record) => {
        const reason =
          record.details.reason || APITypes.TransactionReason.OtherPayment;
        const periodStart = record.details.periodStart;
        const periodEnd = record.details.periodEnd;
        const coverage =
          periodStart && periodEnd
            ? ` (${formatDisplayDateOnly(periodStart)} – ${formatDisplayDateOnly(periodEnd)})`
            : "";
        return (
          <CustomTextField
            value={`${t(reason)}: ${record.details.month + 1}/${
              record.details.year
            }${coverage}`}
          />
        );
      },
    },
    {
      title: t("Receipt"),
      dataIndex: "receipt",
      key: "receipt",
      width: 180,
      render: (_, record) => {
        if (record.details.status !== APITypes.PaymentStatusType.Complete) {
          return null;
        }
        return <ViewReceiptButton transactionID={record.details.id} />;
      },
    },
    {
      title: t("Upload_file"),
      dataIndex: "uploadFile",
      key: "uploadFile",
      width: 200,
      render: (_, record) => {
        return (
          <Button
            onClick={() => handleOpenModal(record.details)}
            disabled={!canTerminateTransaction(record.details)}
            type="primary"
            ghost
          >
            {t("Upload_file")}
          </Button>
        );
      },
    },
    {
      title: t("Created_At"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 250,
      sorter: (a, b) => compareTransactionCreatedAt(a, b),
      sortDirections: ["ascend", "descend"],
      render: (_, record) => {
        return (
          <CustomTextField
            value={dateToLocalTime(record.details.createdAt, "dateTime")}
            prefix={<CalendarOutlined />}
          />
        );
      },
    },
    {
      title: t("Updated_At"),
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 250,
      sorter: (a, b) => compareTransactionUpdatedAt(a, b),
      sortDirections: ["ascend", "descend"],
      render: (_, record) => {
        return (
          <CustomTextField
            value={dateToLocalTime(record.details.updatedAt, "dateTime")}
            prefix={<CalendarOutlined />}
          />
        );
      },
    },
];

export const PaymentTablePage = () => {
  const loaderData = useLoaderData() as PaymentTablePageLoaderData;
  const { t } = useAppTranslation();

  const fetcher = useFetcher();
  const { isTableLoading } = useRouteActivity();
  const isTerminating = fetcher.state !== "idle";

  const [modalTransaction, setModalTransaction] =
    React.useState<APITypes.Transaction | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);

  const sortedTransactions = useMemo(
    () => [...loaderData.transactions].sort(defaultTransactionSort),
    [loaderData.transactions],
  );

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<FormattedTransactionObject> = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: !canTerminateTransaction(record.details),
    }),
  };

  const handleTerminateTransaction = () => {
    const deletedKeys = selectedRowKeys.map(
      (value) => sortedTransactions[value as number].details.id
    );
    fetcher.submit(
      {
        body: JSON.stringify({
          transactionIDs: deletedKeys,
        }),
        intent: INTENTS._terminate_transaction,
      },
      { method: "post" }
    );

    setSelectedRowKeys([]);
  };

  const handleOpenModal = (transaction: APITypes.Transaction) => {
    setModalTransaction(transaction);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalTransaction(null);
  };

  return (
    <>
      <Breadcrumb
        items={[
          {
            title: <HomeOutlined />,
            href: "/",
          },
          {
            title: t("Payments"),
          },
        ]}
      />

      <section style={{ marginTop: "2rem" }}>
        <CustomTable
          tableHeaders={
            <>
              <TableHeaderBar
                left={
                  <TableHeaderInfo
                    title={t("All_Transaction_Info")}
                    count={sortedTransactions.length}
                    countLabel={t("Total_Records_Found")}
                  />
                }
                right={
                  <Space wrap>
                    <Button type="primary" href={ROUTES.paymentsPage}>
                      {t("Make_Payment")}
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleTerminateTransaction}
                      loading={isTerminating}
                      disabled={selectedRowKeys.length === 0 || isTerminating}
                      danger
                    >
                      {t("Terminate")}
                    </Button>
                  </Space>
                }
              />
              <PaymentTablePageFilters filters={loaderData.filters} />
            </>
          }
          mobileCardConfig={{ titleKey: "id", primaryKeys: ["status"] }}
          mobileDefaultSort={{
            dataIndex: ["details", "year"],
            order: "descend",
          }}
          tableProps={{
            dataSource: sortedTransactions.map((value, index) => {
              return {
                key: index,
                ...value,
              };
            }),
            columns: getPaymentTableColumns(t, handleOpenModal),
            rowSelection,
            loading: isTableLoading,
          }}
        />
      </section>

      <Modal
        open={modalOpen}
        onCancel={handleCloseModal}
        footer={null}
        title={`${t("Upload_file")}: ${modalTransaction?.id}`}
      >
        <Card>
          <UploadFileButton
            currentUser={loaderData.currentUser?.uid || ""}
            entityId={modalTransaction?.id || ""}
            entityType={APITypes.EntityType.Transaction}
          />
        </Card>
      </Modal>
    </>
  );
};
